/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/restrict-plus-operands */

import { FreestyleMethodCellData, FreestyleMethodRowData } from "../freestyle-content-form/types";
import { MAX_DECIMALS } from "../types";

/**
 * Rounding to bigger number of decimals between cell value and percentage. If we increase value 12.34 for 34.555 %
 * result should be rounded to 3 decimals. Also if we increase value 12.343423 by 25 %, result should be rounded to
 * 6 decimals.
 * @param quickActionsValue value entered in quick actions field
 * @param cellValue cell value
 * @returns rounding factor when updating all values
 */
export const getRoundingFactor = (quickActionsValue: string, cellValue: string) => {
  // Calculating number of decimal places in quick actions value
  let quickActionsValueDecimals =
    quickActionsValue.indexOf('.') !== -1 ? quickActionsValue.substring(quickActionsValue.indexOf('.') + 1).length : 2;
  if (quickActionsValueDecimals < 2) {
    quickActionsValueDecimals = 2;
  }

  // Calculating number of decimal places in current value
  let cellValueDecimalPlaces = cellValue.indexOf('.') !== -1 ? cellValue.substring(cellValue.indexOf('.') + 1).length : 2;
  if (cellValueDecimalPlaces < 2) {
    cellValueDecimalPlaces = 2;
  }

  const roundingFactor = Math.pow(10, Math.max(quickActionsValueDecimals, cellValueDecimalPlaces));

  return roundingFactor
}
/**
 * Returns true or false if any of the values are selected
 * @param data - FreestyleMethodRowData[] data array
 */
export const isAnythingSelected = (data: FreestyleMethodRowData[]) => {
  for(const year of data) {
    for(const month of year.months) {
      if(month.selected) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Will return true or false dependinding on if we have any selected dates with values
 * @param data - FreestyleMethodRowData[] data array
 */
export const isValueDataAvailable = (data: FreestyleMethodRowData[]) => {
  for(const year of data) {
    for(const month of year.months) {
      if(month.selected && month.value) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Returns a new FreestyleMethodRowData[] with all selected dates having their value changed by the given amount
 * @param data - FreestyleMethodRowData[] data array
 * @param amount - The amount amount to change the selected values by
 * @param mode - Wether to increase or decrease the selected values by the given amount
 */
export const quickActionsChangeByAmount = (data: FreestyleMethodRowData[], amount: string, mode: 'increase' | 'decrease') => {
  return data.map(year => ({
    ...year,
    months: year.months.map(month => {
      let newValue = month.value;
      const roundingFactor = getRoundingFactor(amount, newValue)

      if(month.selected) {
        const adjustment = Number(amount) * (mode === 'increase' ? 1 : -1);
        const adjustedValue = Number(month.value || '0') + adjustment;
        const roundedAdjustedValue = Math.round((adjustedValue + Number.EPSILON) * roundingFactor) / roundingFactor;
        newValue = String(roundedAdjustedValue);
      }

      return {
        ...month,
        value: newValue
      }
    })
  }))
}

/**
 * Returns a new FreestyleMethodRowData[] with all selected dates having their value changed by the given percentage
 * @param data - FreestyleMethodRowData[] data array
 * @param percentage - The percentage amount to change the selected values by
 * @param mode - Wether to increase or decrease the selected values by the given percentage
 */
export const quickActionsChangeByPercentage = (data: FreestyleMethodRowData[], percentage: string, mode: 'increase' | 'decrease') => {
  return data.map(year => ({
    ...year,
    months: year.months.map(month => {
      let newValue = month.value;
      const roundingFactor = getRoundingFactor(percentage, newValue)

      if(month.selected) {
        const adjustment = (mode === 'increase' ? 1 : -1) * Number(month.value || '0') * (Number(percentage) / 100);
        const adjustedValue = Number(month.value || '0') + adjustment;
        const roundedAdjustedValue = Math.round((adjustedValue + Number.EPSILON) * roundingFactor) / roundingFactor;
        newValue = String(roundedAdjustedValue);
      }

      return {
        ...month,
        value: newValue
      }
    })
  }))
}

/**
 * Returns a new FreestyleMethodRowData[] with all selected dates updated to the new value
 * @param data - FreestyleMethodRowData[] data array
 * @param newValue - The new value to set on all selected dates
 */
export const quickActionsSetSelectedTo = (data: FreestyleMethodRowData[], newValue: string) => {
  return data.map(year => ({
    ...year,
    months: year.months.map(month => ({
      ...month,
      value: month.selected ? newValue : month.value
    }))
  }))
}

/**
 * Returns a new FreestyleMethodRowData[] with all selected dates having their values updated.
 * The amount specified is divided equally between all of the selected dates.
 * @param data - FreestyleMethodRowData[] data array
 * @param amount - The new value to divide equally between all selected dates
 */
export const quickActionsSplitBetweenSelected = (data: FreestyleMethodRowData[], amount: string) => {
  const selectedCount = data.reduce((prevTotal, year) => {
    const count = year.months.filter(month => month.selected).length;
    return Number(prevTotal) + Number(count);
  }, 0);
  const splitAmount = Number(amount) / selectedCount;
  const roundingFactor = Math.pow(10, MAX_DECIMALS)
  
  return data.map(year => ({
    ...year,
    months: year.months.map(month => {
      let newValue = month.value;

      if(month.selected) {
        const roundedNewValue = Math.round((splitAmount + Number.EPSILON) * roundingFactor) / roundingFactor;
        newValue = String(roundedNewValue);
      }

      return {
        ...month,
        value: newValue
      }
    })
  }))
}

/**
 * Update the values for a particular FreestyleMethodRowData[] year.
 * Will return a new FreestyleMethodRowData[] with as many existing object references intact as possible to reduce rerenders
 * @param data - current FreestyleMethodRowData[] data
 * @param selectedMonths - FreestyleMethodCellData[] of months that you wish to update
 * @param selectedYear - FreestyleMethodRowData for the year that contains the months to be updated
 * @param callback - Callback function, use this to return your updated data for the given months
 */
export const updateCellDataValues = (
  data: FreestyleMethodRowData[],
  selectedMonths: FreestyleMethodCellData[],
  selectedYear: FreestyleMethodRowData,
  callback: (selectedMonth: FreestyleMethodCellData) => FreestyleMethodCellData
) => {
  const newData: FreestyleMethodRowData[] = [];

  data.forEach(year => {
    if(year.date !== selectedYear.date) {
      newData.push(year);
    }
    else {
      const newMonths: FreestyleMethodCellData[] = [];
      year.months.forEach(month => {
        if(!selectedMonths.find(sm => sm.date === month.date)) {
          newMonths.push(month);
        }
        else {
          newMonths.push(callback(month))
        }
      })
      newData.push({
        ...year,
        months: newMonths
      });
    }
  });

  return newData;
}

/**
 * Will update the given array, updating the isAllSelected flag for each year
 * @param data - FreestyleMethodRowData[] data array
 */
export const updateIsAllSelectedValue = (year: FreestyleMethodRowData) => {
  const count = year.months.filter(month => month.selected || month.readOnly).length;
  year.isAllSelected = count === year.months.length;
}
